import { useEffect, useMemo, useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { coreKeys } from '@saas/module-core/ui';
import { conversationKeys } from '@saas/module-conversation/ui';
import { workflowKeys } from '@saas/module-workflow/ui';
import { resetPresenceCache, useAuth, type RealtimeBus, type RealtimeEvent, type RealtimeStatus } from '@saas/ui';
import { api } from './client.js';
import { tokenStore } from './tokens.js';

/**
 * realtime gateway（apps/realtime・03 §11.3b）への接続。**1 ユーザー 1 接続**で全チャネルを受ける。
 *  - ログイン後に `/ws?token=<accessToken>&since=<最後に受け取った id>&device=web` へ繋ぐ。URL は VITE_REALTIME_URL、
 *    無ければ同一オリジンの /ws（開発サーバでは ws://localhost:4100/ws。Vite の proxy は /ws を持たない）
 *  - トークンは接続時に新しくする（残り 30 秒未満なら /auth/refresh）。切れた（4401）ら refresh して繋ぎ直す
 *  - 指数バックオフ（1 秒 → 最大 30 秒・ジッタ付き）。online / 画面復帰で即座に繋ぎ直す。25 秒ごとの ping、60 秒応答なしで張り直す
 *  - `since` は localStorage（tenant × user ごと）。hello の complete = false（差分を保証できない）なら全部読み直す（resync）
 *  - 受け取った { channel, type, payload } は TanStack Query の invalidate に変換する（下の invalidateFor）。
 *    画面側は @saas/ui の useRealtimeMessages() でも直接受け取れる（chat など）
 */
const PING_INTERVAL_MS = 25_000;
const DEAD_AFTER_MS = 60_000;
const MAX_BACKOFF_MS = 30_000;

interface ControlFrame {
  control: 'hello' | 'pong';
  complete?: boolean;
  replayed?: number;
}

function realtimeUrl(): string {
  const configured = (import.meta.env.VITE_REALTIME_URL as string | undefined)?.trim();
  if (configured) return configured;
  if (import.meta.env.DEV) return 'ws://localhost:4100/ws';
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws`;
}

function sinceKey(userKey: string): string {
  return `saas.realtime.since:${userKey}`;
}

/** JWT の exp（秒）。読めなければ null */
function tokenExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

async function freshToken(): Promise<string | null> {
  const current = tokenStore.getAccess();
  const exp = current ? tokenExpiry(current) : null;
  if (current && exp && exp * 1000 - Date.now() > 30_000) return current;
  const ok = await api.refreshTokens();
  return ok ? tokenStore.getAccess() : null;
}

class RealtimeClient {
  status: RealtimeStatus = 'idle';
  private ws: WebSocket | null = null;
  private userKey: string | null = null;
  private attempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private lastSeen = 0;
  private stopped = true;
  private readonly listeners = new Set<(e: RealtimeEvent) => void>();
  private readonly resyncListeners = new Set<() => void>();
  private readonly statusListeners = new Set<(s: RealtimeStatus) => void>();
  /** 同時編集の部屋のフレーム（{ doc: … }・apps/realtime の doc-rooms.ts） */
  private readonly docListeners = new Set<(frame: Record<string, unknown>) => void>();

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.wake());
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this.wake();
      });
    }
  }

  start(userKey: string): void {
    if (!this.stopped && this.userKey === userKey) return;
    this.stop();
    this.stopped = false;
    this.userKey = userKey;
    this.attempt = 0;
    void this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.clearTimers();
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onclose = null;
      ws.close(1000, 'client closed');
    }
    this.setStatus('closed');
  }

  subscribe(handler: (e: RealtimeEvent) => void): () => void {
    this.listeners.add(handler);
    return () => this.listeners.delete(handler);
  }
  onResync(handler: () => void): () => void {
    this.resyncListeners.add(handler);
    return () => this.resyncListeners.delete(handler);
  }
  onStatus(handler: (s: RealtimeStatus) => void): () => void {
    this.statusListeners.add(handler);
    return () => this.statusListeners.delete(handler);
  }
  subscribeDocFrames(handler: (frame: Record<string, unknown>) => void): () => void {
    this.docListeners.add(handler);
    return () => this.docListeners.delete(handler);
  }

  /** 同時編集の部屋への上りのフレーム。先頭のキーを "doc" にする（realtime はそれで見分ける）。開いていなければ false */
  send(frame: { doc: string; [key: string]: unknown }): boolean {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN || this.status !== 'open') return false;
    const { doc, ...rest } = frame;
    ws.send(JSON.stringify({ doc, ...rest }));
    return true;
  }

  /** online / 画面復帰: 待たずに繋ぎ直す */
  private wake(): void {
    if (this.stopped || this.status === 'open' || this.status === 'connecting') return;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    void this.connect();
  }

  private async connect(): Promise<void> {
    if (this.stopped || !this.userKey) return;
    this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');
    const token = await freshToken();
    if (this.stopped) return;
    if (!token) {
      // リフレッシュできない = ログアウト扱い（AuthProvider が /login へ送る）。少し待ってもう一度だけ試す
      this.scheduleReconnect();
      return;
    }
    const url = new URL(realtimeUrl());
    url.searchParams.set('token', token);
    url.searchParams.set('device', 'web');
    const since = this.readSince();
    if (since) url.searchParams.set('since', since);
    let ws: WebSocket;
    try {
      ws = new WebSocket(url.toString());
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    this.lastSeen = Date.now();
    ws.onmessage = (ev) => this.onMessage(ev.data as string, Boolean(since));
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.clearTimers();
      if (this.stopped) return;
      if (ev.code === 4401) tokenStore.setAccess(null); // 次の接続で必ずリフレッシュする
      // サーバが「混んでいる / 送りすぎ」と言って切った（1013 = テナントの接続上限、1008 = 上りのレート制限）。
      // すぐ繋ぎ直すと同じ理由でまた切られるので、待ち時間を長い側から始める（握手の HTTP 429 は hello が来ないので attempt が積み上がる）
      if (ev.code === 1013 || ev.code === 1008) this.attempt = Math.max(this.attempt, 4);
      this.scheduleReconnect();
    };
    ws.onerror = () => {
      /* close が続いて来る */
    };
    ws.onopen = () => {
      this.pingTimer = setInterval(() => {
        if (Date.now() - this.lastSeen > DEAD_AFTER_MS) {
          ws.close(4000, 'no pong');
          return;
        }
        if (ws.readyState === ws.OPEN) ws.send(JSON.stringify({ control: 'ping' }));
      }, PING_INTERVAL_MS);
    };
  }

  private onMessage(raw: string, hadSince: boolean): void {
    this.lastSeen = Date.now();
    let msg: (ControlFrame & Partial<RealtimeEvent>) | null = null;
    try {
      msg = JSON.parse(raw) as ControlFrame & Partial<RealtimeEvent>;
    } catch {
      return;
    }
    if (!msg) return;
    // 同時編集の部屋のフレーム（id が無いので since には入れない）
    if (typeof (msg as { doc?: unknown }).doc === 'string') {
      for (const l of this.docListeners) {
        try {
          l(msg as unknown as Record<string, unknown>);
        } catch (err) {
          console.warn('[realtime] doc handler failed', err);
        }
      }
      return;
    }
    if (msg.control === 'hello') {
      this.attempt = 0;
      this.setStatus('open');
      if (hadSince && msg.complete === false) for (const l of this.resyncListeners) l();
      return;
    }
    if (msg.control === 'pong') return;
    if (typeof msg.id !== 'string' || typeof msg.channel !== 'string' || typeof msg.type !== 'string') return;
    this.writeSince(msg.id);
    const e = msg as RealtimeEvent;
    for (const l of this.listeners) {
      try {
        l(e);
      } catch (err) {
        console.warn('[realtime] handler failed', err);
      }
    }
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.reconnectTimer) return;
    const base = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** Math.min(this.attempt, 5));
    const delay = Math.round(base * (0.5 + Math.random()));
    this.attempt += 1;
    this.setStatus('reconnecting');
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, delay);
  }

  private clearTimers(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.reconnectTimer = null;
    this.pingTimer = null;
  }

  private setStatus(s: RealtimeStatus): void {
    if (this.status === s) return;
    this.status = s;
    for (const l of this.statusListeners) l(s);
  }

  private readSince(): string | null {
    if (!this.userKey) return null;
    try {
      return localStorage.getItem(sinceKey(this.userKey));
    } catch {
      return null;
    }
  }
  private writeSince(id: string): void {
    if (!this.userKey) return;
    try {
      localStorage.setItem(sinceKey(this.userKey), id);
    } catch {
      /* ignore */
    }
  }
}

/** アプリ全体で 1 つ */
export const realtimeClient = new RealtimeClient();

/**
 * 受け取ったメッセージ → TanStack Query の invalidate。
 *   notify        → 通知の未読数・一覧、承認の受信箱（承認依頼は通知として届く）。会話の通知なら「自分宛て」も
 *   presence      → @saas/ui の usePresence が自分で反映する。自分の分だけ設定画面のクエリを更新
 *   dm / channel / record / record_signal → conversation のキー全部（chat は useRealtimeMessages で細かく拾う）
 *   telephony / meeting → 段 5
 */
export function invalidateFor(qc: QueryClient, e: RealtimeEvent, myUserId: string | null): void {
  const inv = (queryKey: readonly unknown[]): void => void qc.invalidateQueries({ queryKey });
  switch (e.channel) {
    case 'notify': {
      inv(coreKeys.unreadCount());
      inv(['notifications']);
      inv(workflowKeys.inboxCount());
      inv(workflowKeys.inbox());
      const kind = (e.payload as { kind?: unknown } | null)?.kind;
      if (typeof kind === 'string' && kind.startsWith('conversation.')) inv(conversationKeys.me());
      return;
    }
    case 'presence': {
      const userId = (e.payload as { userId?: unknown } | null)?.userId;
      if (myUserId && userId === myUserId) inv(coreKeys.myPresence());
      return;
    }
    case 'dm':
    case 'channel':
    case 'record':
    case 'record_signal':
      inv(conversationKeys.all());
      return;
    case 'task':
      inv(['task']);
      return;
    default:
      return;
  }
}

/**
 * ログイン後に接続し、ログアウトで切る。返り値を @saas/ui の RealtimeProvider に渡す（App.tsx）。
 * 30 秒 / 60 秒のポーリング（nav.ts の refetchInterval）はフォールバックとして残る
 */
export function useRealtime(): RealtimeBus {
  const { status: authStatus, me } = useAuth();
  const qc = useQueryClient();
  const [status, setStatus] = useState<RealtimeStatus>(realtimeClient.status);
  const userKey = authStatus === 'authenticated' && me ? `${me.tenant.id}:${me.user.id}` : null;
  const myUserId = me?.user.id ?? null;

  useEffect(() => realtimeClient.onStatus(setStatus), []);

  useEffect(() => {
    if (!userKey) {
      realtimeClient.stop();
      return;
    }
    realtimeClient.start(userKey);
    const off = realtimeClient.subscribe((e) => invalidateFor(qc, e, myUserId));
    const offResync = realtimeClient.onResync(() => {
      resetPresenceCache();
      void qc.invalidateQueries();
    });
    return () => {
      off();
      offResync();
    };
  }, [userKey, myUserId, qc]);

  return useMemo<RealtimeBus>(
    () => ({
      status,
      subscribe: (h) => realtimeClient.subscribe(h),
      onResync: (h) => realtimeClient.onResync(h),
      // 同時編集の部屋（wiki の編集画面・閲覧画面の在席）
      send: (frame) => realtimeClient.send(frame),
      subscribeDocFrames: (h) => realtimeClient.subscribeDocFrames(h),
    }),
    [status],
  );
}
